import { captureError } from './sentry';
import { toast } from '@/hooks/use-toast';

// Global unhandled promise rejection handler
export function setupGlobalErrorHandlers() {
  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    
    captureError(
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      {
        type: 'unhandledRejection',
        promiseRejection: true,
      }
    );

    // Show user-friendly error
    toast({
      title: 'An unexpected error occurred',
      description: 'We\'ve been notified and will look into it.',
      variant: 'destructive',
    });

    event.preventDefault(); // Prevent default browser error handling
  });

  // Global error handler
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
    
    captureError(event.error || new Error(event.message), {
      type: 'globalError',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });

    event.preventDefault();
  });
}

// Network error interceptor for Supabase
export function handleSupabaseError(error: any, operation: string) {
  const errorMessage = error?.message || 'An error occurred';
  
  // Log to Sentry with context
  captureError(
    error instanceof Error ? error : new Error(errorMessage),
    {
      operation,
      supabaseError: true,
      code: error?.code,
      details: error?.details,
    }
  );

  // Return user-friendly message
  if (errorMessage.includes('Failed to fetch')) {
    return 'Network error. Please check your connection and try again.';
  }
  
  if (errorMessage.includes('JWT')) {
    return 'Your session has expired. Please sign in again.';
  }
  
  if (error?.code === 'PGRST116') {
    return 'The requested resource was not found.';
  }
  
  if (error?.code === '23505') {
    return 'This record already exists.';
  }
  
  if (error?.code === '23503') {
    return 'Cannot delete: this item is referenced by other records.';
  }

  return 'An error occurred. Please try again.';
}

// API error parser
export function parseAPIError(error: any): string {
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  if (error?.error) return error.error;
  return 'An unexpected error occurred';
}
